'use strict';

var App = require('app');
var AppState = require('app_state');
var Consts = require('consts');
var DataEditDialogView = require('components/devices/views/common/data_edit_dialog_view');
var Decorator = require('utils/decoration');
var i18n = require('utils/i18n');
var Misc = require('utils/misc');
var Notification = require('utils/notification');
var Value = require('models/values');

var SchemaSidebarView = Marionette.ItemView.extend({
    template: require('components/scheme/templates/sidebar'),

    ui: {
        btnConfigValue: '.js-value-config-button',
        btnConfigHCs: '.js-hcs-config-button',
        btnSmartTemp: '.js-smart-temp-button',
        btnEditValue: '.js-value-edit-button',
        btnRemoveValue: '.js-value-remove-button',
        deviceValues: '#js-device-values',
    },

    events: {
        'click @ui.btnConfigValue': 'configureValues',
        'click @ui.btnConfigHCs': 'configureHCs',
        'click @ui.btnSmartTemp': 'smartTempDialog',
        'click @ui.btnEditValue': 'writeValue',
        'click @ui.btnRemoveValue': 'removeValue',
    },

    initialize: function (options) {
        this.sensorValues = options.sensorValues || [];
        this.device = options.device;
        this.controller = options.controller;
        this.columns = options.columns;
        this.configuredUserValues = options.valueList; // all configs for user-defined values
        this.sidebarConfig = options.sidebarConfig || []; // all data points chosen for the sidebar
        this.values = options.values; // all fetched device values
        this.valuesFetched = options.valuesFetched;
        this.canWriteValues = options.canWriteValues;
        this.canConfigureValues = options.canConfigureValues;
        this.hasTabs = options.hasTabs;
        this.smartTemp = options.smartTempInfo;
        this.hcsConfig = options.hcsConfig || {};

        if (this.configuredUserValues != undefined) {
            var modelsToRemove = this.configuredUserValues.filter(function (config) {
                // do not show elements with length > 8, e.g. time programs
                if (config.get('length') > 8 && config.get('format') != 'ascii') {
                    return true;
                }

                // do not show protected elements to regular users
                if (config.get('is_protected') && !AppState.user.get('is_admin')) {
                    return true;
                }
            });
            this.configuredUserValues.remove(modelsToRemove);

            this.rvtValues = this.configuredUserValues.filter(function (model) {
                return model.get('transfer_mode') !== Consts.TransferMode.None;
            });
        }
    },

    getCircuitNames: function () {
        var names = this.device.getCircuitNames();
        names['external'] = i18n.TR('Externe Bedarfsanforderung');
        return names;
    },

    serializeData: function () {
        var data = {
            canWriteValues: this.canWriteValues,
            canConfigureValues: this.canConfigureValues,
            hasTabs: this.hasTabs,
            hasValues: this.configuredUserValues != undefined,
            canSeeConfig: this.sidebarConfig.length > 0 || this.canConfigureValues,
            deviceID: this.device.id,
        };

        // formatSensorData gets the name and unit for each value
        var self = this;
        function formatSensorData(sensor, filter, config) {
            sensor.displayData = [];
            if (filter) {
                sensor.data = _.pick(sensor.data, filter);
            }
            _.each(sensor.data, function (value, key) {
                var result = self.getInfos(value, key, config);
                if (result != undefined) {
                    result.name = i18n.DATAPOINT(sensor.category, result.name);
                    sensor.displayData.push(result);
                }
            });
            sensor.displayData = _.sortBy(sensor.displayData, 'name');
        }

        data.valuesFetched = this.valuesFetched;
        if (this.valuesFetched && data.canSeeConfig) {
            data.deviceValues = this.device.attributes;
            data.deviceValues.data = this.values;
            formatSensorData(data.deviceValues, this.sidebarConfig, this.configuredUserValues);
        }

        AppState.requireCompanyFeatures(function () {
            data.smartTempFeatureActivated = AppState.Cache.companyFeatures[Consts.Features.SmartTemp];
        });

        data.sensor = [];
        for (var i = 0; i < this.sensorValues.length; i++) {
            var sensor = this.sensorValues[i];

            sensor.displayData = [];
            sensor.modelType = 'device';

            _.each(sensor.data, function (value, key) {
                var format = sensor.formatInfos[key] || { name: key };
                var result = Misc.getValueDisplayInfos(
                    value,
                    key,
                    format.format,
                    format.unit,
                    format.display_unit,
                    format.is_unit_fixed
                );

                sensor.displayData.push({
                    id: key,
                    nr: parseInt(key.split('_')[1]),
                    name: i18n.DATAPOINT(sensor.category, format.name),
                    unit: result.unit,
                    value: result.value,
                });
            });

            sensor.displayData = _.sortBy(sensor.displayData, 'name');

            // smart temp info for each meter:
            var circuitNames = this.getCircuitNames();
            sensor.hcs = [];
            _.each(this.hcsConfig[sensor.id], function (hc) {
                sensor.hcs.push(circuitNames[hc] || Consts.DefaultCircuitNames[hc]);
            });

            if (self.smartTemp) {
                var activeMeters = self.smartTemp.getActiveMeterIDs(true);
                var matchingModel = self.smartTemp.findModelWithMatchingHCs(sensor.id, self.hcsConfig[sensor.id] || []);
                var alreadyOptimizedHCs = self.smartTemp.getOptimizedHCs(true);
                var alreadyOptimizedRegs = self.smartTemp.getOptimizedRegisters(true);
                sensor.smartTemp = {
                    isActive: activeMeters.includes(sensor.id) && self.smartTemp.isActive(),
                    isDryRun: activeMeters.includes(sensor.id) && self.smartTemp.isDryRun(),
                    noSelection: self.hcsConfig[sensor.id] === undefined || self.hcsConfig[sensor.id].length === 0,
                    isPossible:
                        matchingModel !== undefined &&
                        matchingModel.get('meter_position').every(function (hc) {
                            return !alreadyOptimizedHCs.includes(hc);
                        }) &&
                        !alreadyOptimizedRegs.includes('MBR_10'),
                    // ensures none of the HCs in consideration is already used for smart temp
                    // if outdoor sensor is used, we can not use smart temp for multiple circuits independently
                };

                sensor.smartTempBtnText = sensor.smartTemp.isActive
                    ? i18n.TR('Smart Temp aktiv')
                    : sensor.smartTemp.isDryRun
                      ? i18n.TR('Smart Temp-Vorschau aktiv')
                      : sensor.smartTemp.noSelection
                        ? i18n.TR('keine Heizkreise zugeordnet')
                        : sensor.smartTemp.isPossible
                          ? i18n.TR('Smart Temp')
                          : i18n.TR('Smart Temp nicht anwendbar');
            }

            if (sensor.displayData.length > 0 || sensor.category === 'Meter') {
                data.sensor.push(sensor);
            }
        }
        data.device = this.device.attributes;
        return data;
    },

    onRender: function () {
        this.showOrHideSpinner(!this.valuesFetched);
    },

    onShow: function () {
        this.showOrHideSpinner(!this.valuesFetched);
    },

    update: function (values) {
        this.values = values;
        this.valuesFetched = true;
        this.render();
    },

    showOrHideSpinner: function (show) {
        Decorator.displayLoading(show, '#js-device-values');
    },

    configureValues: function (e) {
        if (!this.canConfigureValues) {
            return;
        }

        var self = this;
        var availableValues = new Value.Collection(
            this.configuredUserValues.filter(function (model) {
                return self.sidebarConfig.includes(model.get('type') + '_' + model.get('nr'));
            })
        );

        this.controller.showSelectValuesDialog({
            deviceID: this.device.id,
            group: this.device.getGroupFilter(),
            AllowedValueTypes: [],
            showUpdateClassInfo: false,
            showUpdateClassDropdown: false,
            useCustomValues: false,
            values: availableValues,
            fakeResponse: this.configuredUserValues.getAsResponse(this.device.get('category')),

            update_cb: function (changes, values) {
                // update collection
                self.sidebarConfig = [];
                _.each(values, function (v) {
                    v.id = v.type + '_' + v.nr;
                    self.sidebarConfig.push(v.id);
                });
                self.sidebarConfig = _.unique(self.sidebarConfig);
                self.showOrHideSpinner(true);
                var sidebarConfig = App.request('device:scheme-config', [self.device.id], self.sidebarConfig);
                $.when(sidebarConfig).done(function (resp) {
                    if (resp !== false) {
                        var valueMap = {};
                        valueMap[self.device.id] = self.sidebarConfig;

                        self.controller.getRegisters(valueMap, undefined, function (resp) {
                            if (resp.valuemap !== undefined) {
                                self.values = resp.valuemap[self.device.id];
                            }
                            // sent update to layout view
                            self.triggerMethod('updated:sideBarConfig', self.sidebarConfig);
                            self.render();

                            self.showOrHideSpinner(false);
                        });
                    } else {
                        Notification.show(
                            'error',
                            i18n.TR('Fehler'),
                            i18n.TR('Wertauswahl konnte nicht aktualisiert werden.')
                        );
                        self.showOrHideSpinner(false);
                    }
                });
            },
        });
    },

    configureHCs: function (e) {
        var self = this;
        var available = this.hcsConfig['available'];
        if (!available.includes('external')) {
            available = available.concat(['external']);
        }
        var controllerID = this.device.id;
        var meterID = parseInt(e.target.id || e.target.parentNode.id);
        this.controller.showSelectHCsDialog({
            controllerID: controllerID,
            meterID: meterID,
            availableHCs: available,
            selectedHCs: this.hcsConfig[meterID],
            hcNames: this.getCircuitNames(),
            save_cb: function (selectedHCs) {
                var request = App.request('device:hcs-config', controllerID, meterID, selectedHCs);
                $.when(request).done(function (resp) {
                    if (resp !== false) {
                        self.hcsConfig[meterID] = selectedHCs;
                        self.render();
                        self.showOrHideSpinner(false);
                    } else {
                        Notification.show(
                            'error',
                            i18n.TR('Fehler'),
                            i18n.TR('Zählerposition konnte nicht aktualisiert werden.')
                        );
                        self.showOrHideSpinner(false);
                    }
                });
            },
        });
    },

    smartTempDialog: function (e) {
        var self = this;
        var controllerID = this.device.id;
        var meterID = parseInt(e.target.id || e.target.parentNode.id);
        this.controller.showSmartTempDialog({
            smartTempInfo: this.smartTemp,
            controllerID: controllerID,
            meterID: meterID,
            meterName: _.find(this.sensorValues, { id: meterID }).name,
            rvtValues: this.rvtValues,
            circuits: this.hcsConfig[meterID],
            circuitNames: this.getCircuitNames(),
            save_cb: function (selectedHCs) {
                var request = App.request('device:hcs-config', controllerID, meterID, self.hcsConfig[meterID]);
                $.when(request).done(function (resp) {
                    if (resp !== false) {
                        self.hcsConfig[meterID] = selectedHCs;
                        self.render();
                        self.showOrHideSpinner(false);
                    } else {
                        Notification.show(
                            'error',
                            i18n.TR('Fehler'),
                            i18n.TR('Zählerposition konnte nicht aktualisiert werden.')
                        );
                        self.showOrHideSpinner(false);
                    }
                });
            },
        });
    },

    writeValue: function (e) {
        if (!this.canWriteValues) {
            return;
        }
        var address = $(e.target).parents('dt')[0].id.split('_');
        var valueCfg = this.options.valueList.findWhere({ nr: parseInt(address[1]), type: address[0] });
        valueCfg.attributes.data = this.device.attributes.data;
        // we cannot require this controller at the beginning of the file, because of require circles
        var BaseDeviceController = require('components/devices/controllers/base_device_controller');
        var self = this;
        // check if it is possible to edit this value
        App.request('device:values:write-info', this.device.id, address[1]).done(function (writeInfo) {
            if (!writeInfo) {
                Notification.show(
                    'warning',
                    i18n.TR('Warnung'),
                    i18n.TR('Schreibbefehl kann nicht ausgeführt werden.')
                );
                return;
            }
            App.showModal(
                new DataEditDialogView.Dialog({
                    model: valueCfg,
                    device: this.device,
                    controller: new BaseDeviceController({ deviceCategory: '55xx56xx' }),
                    writeInfo: writeInfo,
                    callback: function (model) {
                        self.showOrHideSpinner(false);
                        // trigger refresh schema. Fake event
                        self.triggerMethod('wrote:value');
                    },
                    closeCallback: function () {
                        self.showOrHideSpinner(false);
                    },
                    saveCallback: function () {
                        self.showOrHideSpinner(true);
                    },
                })
            );
        });
    },

    getInfos: function (value, key, columns) {
        var keyParts = key.split('_');
        var infos = $.extend(true, {}, _.find(columns, { id: key }));
        if ($.isEmptyObject(infos)) {
            if (!columns.find) {
                return undefined;
            }
            var model = columns.find({ nr: parseInt(keyParts[1]), type: keyParts[0] });
            if (!model) {
                return undefined;
            }
            infos = $.extend(true, {}, model.attributes);
            infos.id = key;
        }

        if (infos != undefined) {
            var result = Misc.getValueDisplayInfos(
                value,
                infos.id,
                infos.format,
                infos.unit,
                infos.display_unit,
                infos.is_unit_fixed
            );
            infos.unit = result.unit;
            infos.value = result.value;
            infos.nr = parseInt(key.split('_')[1]);
            return infos;
        }
    },
});

module.exports = SchemaSidebarView;
